import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea.tsx';
import TranslationToggle from 'dibs-elements/exports/TranslationToggle.tsx';

import {
    useItemTranslations,
    INTERFACE_STATE,
} from '../PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider.tsx';
import { trackTranslationToggle } from '../tracking/sharedTranslationToggleTracking.ts';
import {
    pdpAccordionReadLessLabel,
    pdpAccordionReadMoreButtonClass,
    pdpAccordionReadMoreLabel,
    pdpAccordionReadMoreWrapperClass,
} from './PdpRespAccordionReadMoreLabels.tsx';

import { type PdpRespAboutItemPanelItemModuleDescription_item$key } from './__generated__/PdpRespAboutItemPanelItemModuleDescription_item.graphql.ts';

const itemFragment = graphql`
    fragment PdpRespAboutItemPanelItemModuleDescription_item on Item {
        seoDescription
    }
`;

const trimTrailingHtmlBreaks = (html: string): string =>
    html.replace(/(?:<br\s*\/?>\s*)+$/i, '').trim();

type Props = {
    item: PdpRespAboutItemPanelItemModuleDescription_item$key;
    showExpandingArea?: boolean;
};

export const PdpRespAboutItemPanelItemModuleDescription: FC<Props> = ({
    item: itemRef,
    showExpandingArea = true,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const [state, dispatch] = useItemTranslations();

    if (!item?.seoDescription) {
        return null;
    }

    const showOriginal = state?.interfaceState === INTERFACE_STATE.SHOW_ORIGINAL;
    const descriptionToShow = showOriginal
        ? state?.data?.viewer?.item?.seoDescription
        : item.seoDescription;
    const trimmedDescription = (descriptionToShow || '').trim();
    const htmlForDisplay = trimTrailingHtmlBreaks(trimmedDescription);
    const descriptionContent = (
        <span
            className={classnames(
                dibsCss.block,
                dibsCss.whitespaceNormal,
                dibsCss.textNoir,
                dibsCss.sassyFontBodyMedium
            )}
            data-tn="pdp-item-description-content"
            dangerouslySetInnerHTML={{ __html: htmlForDisplay }}
        />
    );

    return (
        <div data-tn="pdp-item-description">
            {state && dispatch && (
                <div className={dibsCss.mbXsmall}>
                    <TranslationToggle
                        currentState={state.interfaceState}
                        onClick={() => {
                            trackTranslationToggle();
                            dispatch({ type: 'TOGGLE_TRANSLATIONS' });
                        }}
                    />
                </div>
            )}
            {showExpandingArea ? (
                <ExpandingAreaWithReadMore
                    readMore={pdpAccordionReadMoreLabel}
                    readLess={pdpAccordionReadLessLabel}
                    collapsedHeight={72}
                    lineClamp={3}
                    showGradient={false}
                    disableScrollOnCollapse
                    wrapperClass={pdpAccordionReadMoreWrapperClass}
                    buttonClass={pdpAccordionReadMoreButtonClass}
                    buttonUnderline="none"
                >
                    {descriptionContent}
                </ExpandingAreaWithReadMore>
            ) : (
                descriptionContent
            )}
        </div>
    );
};
